import el from "@cypherpotato/el";
import { api } from "../api";
import { copyToClipboard } from "../ui/clipboard";
import { formatFlexibibleCurrency, formatNumber } from "../ui/format";
import { matchesOutputPriceFilter } from "../ui/model-pricing";
import { runOperation } from "../ui/operation";
import { throttledCall } from "../ui/throttle";
import { cond, pipe } from "../util";
import { Badge, BadgeList, TagIconBadge } from "./badge";
import { FormControl, Select, TextInput } from "./form";
import { Icon } from "./icon";
import { ModelIcon, ProviderBadge } from "./model-icon";
import { MutedText } from "./text";
import { TabControl } from "./tabcontrol";
import { Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "./table";

const enumValue = value => Array.isArray(value) ? value[0] : value;
const shortSize = size => cond(
    [size >= 1_000_000, `${(size / 1_000_000).toFixed(0)}M`],
    [size >= 1_000, `${(size / 1_000).toFixed(0)}K`],
    [true, formatNumber(size)]
);
const rankItems = (items, search, searchableText) => search ?
    items
        .map(item => ({
            item,
            rank: searchableText(item).toLowerCase().split("\n").reduce((rank, value, index) =>
                rank || (value.includes(search.toLowerCase()) ? index + 1 : 0), 0)
        }))
        .filter(result => result.rank)
        .sort((left, right) => left.rank - right.rank)
        .map(result => result.item) :
    items;
const RoutingOptions = ["Balanced", "Cheapest", "Fastest", "Quality", "Special"];
const routeProviders = (model, route) => pipe(
    (model.providers || []).filter(provider => !provider.isDisabled),
    providers => cond(
        [route == "Special", providers],
        [!route || route == "Balanced", pipe(providers.filter(provider => enumValue(provider.group) == "Default"), defaults =>
            defaults.length ? defaults : providers.filter(provider => enumValue(provider.group) != "Special"))],
        [RoutingOptions.includes(route), providers.filter(provider => enumValue(provider.group) != "Special")],
        [true, providers.filter(provider => provider.routingTag?.toLowerCase() == route.toLowerCase())]
    )
);
const lowestPrice = (model, key) => pipe(
    routeProviders(model, "Cheapest").map(provider => provider.pricing?.[0]?.[key]).filter(value => value != null),
    prices => prices.length ? Math.min(...prices) : model.pricing?.[0]?.[key]
);
const valueRange = (values, format) => pipe(
    values.filter(value => value != null),
    known => known.length ? pipe([Math.min(...known), Math.max(...known)], ([min, max]) =>
        min == max ? format(min) : `${format(min)} – ${format(max)}`) : null
);
const ModelPrice = price => el("dd.model-picker-price",
    el("strong", price),
    MutedText("/M tokens")
);
const ModelCapability = capability => cond(
    [capability == "Thinking", TagIconBadge(Icon("lightbulb-line"), "Reasoning", "var(--fr-200)", { title: "Reasoning" })],
    [capability == "ToolCalling", TagIconBadge(Icon("formula"), "Tools", "var(--fr-200)", { title: "Tool calling" })],
    [capability == "ImageInput", TagIconBadge(Icon("image-line"), "Images", "var(--fr-200)", { title: "Image input" })],
    [capability == "AudioInput", TagIconBadge(Icon("volume-up-line"), "Audio", "var(--fr-200)", { title: "Audio input" })],
    [capability == "VideoInput", TagIconBadge(Icon("film-line"), "Video", "var(--fr-200)", { title: "Video input" })],
    [capability == "FileInput", TagIconBadge(Icon("file-text-line"), "Files", "var(--fr-200)", { title: "File input" })],
    [capability == "Uncensored", TagIconBadge(Icon("eye-close-line"), "Uncensored", "var(--fr-200)", { title: "Uncensored" })],
    [capability == "ModelRouter", TagIconBadge(Icon("route-line"), "Model routing", "var(--fr-200)", { title: "Model routing" })],
    [capability == "Diffusion", TagIconBadge(Icon("qr-scan-2-line"), "Diffusion", "var(--fr-200)", { title: "Diffusion model" })],
    [true, TagIconBadge(Icon("checkbox-circle-line"), capability.replace(/([a-z])([A-Z])/g, "$1 $2"), "var(--fr-200)", { title: capability.replace(/([a-z])([A-Z])/g, "$1 $2") })]
);

export function ModelPickerControl(data) {
    const initialValue = data?.selectedModel ?? data?.value;
    const models = el.state.create([]);
    const gateways = el.state.create([]);
    const selectedModel = el.state.create(null);
    const selectedGateway = el.state.create(null);
    const selectedType = el.state.create("model");
    const selectedRoute = el.state.create("");
    const filterText = el.state.create("");
    const filterGatewayText = el.state.create("");
    const filterProvider = el.state.create("");
    const filterIntelligence = el.state.create("");
    const filterSpeed = el.state.create("");
    const filterPrice = el.state.create("");
    const filterCapability = el.state.create("");
    const sortBy = el.state.create("date");
    let modelPicker;
    let activeFinderOption = -1;

    const routedName = () => selectedModel.value && (selectedRoute.value ?
        `${selectedModel.value.name}#${RoutingOptions.includes(selectedRoute.value) ? selectedRoute.value.toLowerCase() : selectedRoute.value}` :
        selectedModel.value.name);

    const handleFinderKey = event => {
        if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) {
            return;
        }

        const options = [...event.currentTarget.closest(".model-picker-finder").querySelectorAll(".model-picker-option")];
        if (!options.length) {
            return;
        }

        event.preventDefault();
        if (event.key == "Enter") {
            options[Math.min(Math.max(activeFinderOption, 0), options.length - 1)].click();
            return;
        }

        activeFinderOption = event.key == "ArrowDown" ?
            (activeFinderOption + 1) % options.length :
            (activeFinderOption - 1 + options.length) % options.length;
        options.forEach((option, index) => option.classList.toggle("model-picker-option-active", index == activeFinderOption));
        options[activeFinderOption].scrollIntoView({ block: "nearest" });
    };

    const renderModelFinder = () => el(".model-picker-finder",
        FormControl(null,
            el(".model-picker-search",
                Icon("search-line"),
                TextInput(null, "Search models...", {
                    ariaLabel: "Search models",
                    onInput(event) {
                        activeFinderOption = -1;
                        throttledCall(() => filterText.value = event.target.value, 50);
                    },
                    onKeyDown: handleFinderKey
                })
            )
        ),
        el.state.listening([models, filterProvider, filterIntelligence, filterSpeed, filterPrice, filterCapability, sortBy], () =>
            el(".model-picker-filters",
                FormControl("Provider",
                    Select({
                        value: filterProvider.value,
                        ariaLabel: "Filter by provider",
                        items: [
                            { value: "", text: "All providers" },
                            ...models.value.map(provider => ({ value: provider.provider, text: provider.provider.replace(/^@/, "") }))
                        ],
                        onChange(event) {
                            filterProvider.value = event.target.value;
                            activeFinderOption = -1;
                        }
                    })
                ),
                FormControl("Intelligence",
                    Select({
                        value: filterIntelligence.value,
                        ariaLabel: "Filter by intelligence",
                        items: [
                            { value: "", text: "All intelligence" },
                            { value: "Highest" },
                            { value: "High" },
                            { value: "Medium" },
                            { value: "Low" },
                            { value: "Lowest" }
                        ],
                        onChange(event) {
                            filterIntelligence.value = event.target.value;
                            activeFinderOption = -1;
                        }
                    })
                ),
                FormControl("Speed",
                    Select({
                        value: filterSpeed.value,
                        ariaLabel: "Filter by speed",
                        items: [
                            { value: "", text: "All speeds" },
                            { value: "Ultrafast", text: "Ultra fast" },
                            { value: "Fast" },
                            { value: "Medium" },
                            { value: "Slow" },
                            { value: "Slowest" }
                        ],
                        onChange(event) {
                            filterSpeed.value = event.target.value;
                            activeFinderOption = -1;
                        }
                    })
                ),
                FormControl("Price",
                    Select({
                        value: filterPrice.value,
                        ariaLabel: "Filter by price",
                        items: [
                            { value: "", text: "All prices" },
                            { value: "free", text: "Free" },
                            { value: "discounted", text: "Discounted" },
                            { value: "cheapest", text: "≤ $0.50 output" },
                            { value: "cheap", text: "$0.50–$1 output" },
                            { value: "midrange", text: "$1–$3 output" },
                            { value: "costly", text: "$3–$15 output" },
                            { value: "expensive", text: "> $15 output" }
                        ],
                        onChange(event) {
                            filterPrice.value = event.target.value;
                            activeFinderOption = -1;
                        }
                    })
                ),
                FormControl("Capability",
                    Select({
                        value: filterCapability.value,
                        ariaLabel: "Filter by capability",
                        items: [
                            { value: "", text: "All capabilities" },
                            { value: "Thinking", text: "Reasoning" },
                            { value: "ToolCalling", text: "Tools" },
                            { value: "ImageInput", text: "Images" },
                            { value: "AudioInput", text: "Audio" },
                            { value: "VideoInput", text: "Video" },
                            { value: "FileInput", text: "Files" },
                            { value: "ModelRouter", text: "Model routing" },
                            { value: "Diffusion" },
                            { value: "Uncensored" }
                        ],
                        onChange(event) {
                            filterCapability.value = event.target.value;
                            activeFinderOption = -1;
                        }
                    })
                ),
                FormControl("Sort",
                    Select({
                        value: sortBy.value,
                        ariaLabel: "Sort models",
                        items: [
                            { value: "date", text: "Publish date" },
                            { value: "provider", text: "Provider" }
                        ],
                        onChange(event) {
                            sortBy.value = event.target.value;
                            activeFinderOption = -1;
                        }
                    })
                ),
                [filterProvider.value, filterIntelligence.value, filterSpeed.value, filterPrice.value, filterCapability.value].some(Boolean) && el("button.model-picker-clear-filters[type=button]",
                    {
                        onClick() {
                            filterProvider.value = "";
                            filterIntelligence.value = "";
                            filterSpeed.value = "";
                            filterPrice.value = "";
                            filterCapability.value = "";
                            activeFinderOption = -1;
                        }
                    },
                    "Clear filters"
                )
            )
        ),
        el(".model-picker-options", el.state.listening([models, filterText, filterProvider, filterIntelligence, filterSpeed, filterPrice, filterCapability, sortBy, selectedModel, selectedType], () =>
            el(".model-picker-options-content",
                ...pipe(
                    models.value.map(provider => ({
                        ...provider,
                        models: rankItems(
                            provider.models.filter(model =>
                                (!data?.filterCapability || model.capabilities?.includes(data.filterCapability)) &&
                                (!filterProvider.value || provider.provider == filterProvider.value) &&
                                (!filterIntelligence.value || enumValue(model.intelligenceLevel) == filterIntelligence.value) &&
                                (!filterSpeed.value || enumValue(model.speedLevel) == filterSpeed.value) &&
                                (!filterCapability.value || model.capabilities?.includes(filterCapability.value)) &&
                                (!filterPrice.value ||
                                    filterPrice.value == "free" && model.flags?.isFree ||
                                    filterPrice.value == "discounted" && (model.flags?.isDiscounted || model.isDiscounted) ||
                                    !["free", "discounted"].includes(filterPrice.value) && (
                                        matchesOutputPriceFilter(model, filterPrice.value) ||
                                        model.providers?.some(provider => matchesOutputPriceFilter(provider, filterPrice.value))
                                    ))
                            ),
                            filterText.value,
                            model => `${model.name}\n${provider.provider}\n${model.description || ""}`
                        )
                    })),
                    groups => sortBy.value == "provider" ?
                        groups.filter(provider => provider.models.length) :
                        [{ models: groups.flatMap(provider => provider.models).sort((left, right) => (right.order || 0) - (left.order || 0)) }]
                            .filter(group => group.models.length)
                )
                    .map(provider => el(".model-picker-group",
                        provider.provider && el(".model-picker-group-title", provider.provider),
                        ...provider.models.map(model => el("button.model-picker-option[type=button]",
                            {
                                classList: [selectedType.value == "model" && selectedModel.value?.name == model.name && "model-picker-option-selected"],
                                ariaPressed: (selectedType.value == "model" && selectedModel.value?.name == model.name).toString(),
                                onClick() {
                                    if (selectedModel.value?.name != model.name) {
                                        selectedRoute.value = "";
                                    }
                                    selectedType.value = "model";
                                    selectedModel.value = model;
                                    selectedGateway.value = null;
                                }
                            },
                            el(".model-picker-option-icon", ModelIcon(model.name)),
                            el(".model-picker-option-content",
                                el(".model-picker-option-title",
                                    el("strong", model.name.split("/").at(-1)),
                                    el("small", [sortBy.value == "date" && model.name.split("/")[0], model.modelInformation?.releaseDate].filter(Boolean).join(" · "))
                                ),
                                el("span", model.description || model.name),
                                el(".model-picker-option-details",
                                    model.capabilities?.some(capability => capability != "None") && el(".model-picker-option-capabilities",
                                        ...model.capabilities.filter(capability => capability != "None").map(ModelCapability)
                                    ),
                                    el(".model-picker-option-pricing", { title: "Lowest prices per million tokens among available providers." },
                                        ...[["In", "inputPerMillionTokens"], ["Cache", "cachedInputPerMillionTokens"], ["Out", "outputPerMillionTokens"]].map(([label, key]) =>
                                            el("span",
                                                el("small", label),
                                                el("strong", pipe(lowestPrice(model, key), price => price != null ? formatFlexibibleCurrency(price) : "—"))
                                            )
                                        ),
                                        el("small.model-picker-option-pricing-unit", "/1M")
                                    )
                                )
                            ),
                            el(".model-picker-option-badges",
                                model.flags?.isFree && Badge("Free", "var(--success)"),
                                (model.flags?.isDiscounted || model.isDiscounted) && Badge(
                                    model.meanDiscount > 0 && model.meanDiscount < 1 ? `${formatNumber(model.meanDiscount * 100)}% off` : "Discount",
                                    "var(--success)"
                                ),
                                model.flags?.isDeprecating && Badge("Deprecating", "var(--warn)"),
                                selectedType.value == "model" && selectedModel.value?.name == model.name && Icon("check-line")
                            )
                        ))
                    )).nullWhenEmpty() || [el(".model-picker-empty", MutedText("No models found."))]
            )
        ))
    );

    runOperation("Fetching models...", async () => {
        const [modelsResponse, gatewaysResponse] = await Promise.all([
            api.getJson("/api/v1/information/models.json"),
            data?.listGateways ? api.getJson("/api/v1/ai-gateways") : Promise.resolve({ data: [] })
        ]);
        models.value = modelsResponse.data;
        gateways.value = gatewaysResponse.data;

        if (initialValue) {
            const [initialName, initialRoute = ""] = initialValue.split("#");
            selectedModel.value = models.value
                .flatMap(provider => provider.models)
                .find(model => model.name == initialName) || null;
            selectedRoute.value = RoutingOptions.find(option => option.toLowerCase() == initialRoute.toLowerCase()) || initialRoute;
            selectedGateway.value = gateways.value
                .find(gateway => gateway.slug == initialValue || gateway.id == initialValue) || null;
            selectedType.value = selectedGateway.value ? "gateway" : "model";
            if (selectedGateway.value && modelPicker?.querySelector(".tab-control")) {
                modelPicker.querySelector(".tab-control").currentTab = "AI Gateways";
            }
        }
    });

    return el(".model-picker",
        {
            init() {
                modelPicker = this;
            },
            $properties: {
                value: {
                    get() {
                        return selectedType.value == "gateway" ? selectedGateway.value?.slug : routedName() ?? initialValue;
                    }
                }
            }
        },
        el(".model-picker-layout",
            el(".model-picker-browser",
                data?.listGateways ?
                    TabControl([
                        {
                            title: "Routed Model",
                            contents: renderModelFinder()
                        },
                        {
                            title: "AI Gateways",
                            contents: el(".model-picker-finder",
                                FormControl(null,
                                    el(".model-picker-search",
                                        Icon("search-line"),
                                        TextInput(null, "Search gateways...", {
                                            ariaLabel: "Search gateways",
                                            onInput(event) {
                                                activeFinderOption = -1;
                                                throttledCall(() => filterGatewayText.value = event.target.value, 50);
                                            },
                                            onKeyDown: handleFinderKey
                                        })
                                    )
                                ),
                                el(".model-picker-options", el.state.listening([gateways, filterGatewayText, selectedGateway, selectedType], () =>
                                    el(".model-picker-options-content",
                                        ...rankItems(
                                            gateways.value,
                                            filterGatewayText.value,
                                            gateway => `${gateway.name || ""}\n${gateway.slug || ""}\n${gateway.modelName || ""}\n${gateway.inferenceEndpoint || ""}`
                                        ).map(gateway => el("button.model-picker-option[type=button]",
                                            {
                                                classList: [selectedType.value == "gateway" && selectedGateway.value?.slug == gateway.slug && "model-picker-option-selected"],
                                                ariaPressed: (selectedType.value == "gateway" && selectedGateway.value?.slug == gateway.slug).toString(),
                                                onClick() {
                                                    selectedType.value = "gateway";
                                                    selectedGateway.value = gateway;
                                                    selectedModel.value = null;
                                                }
                                            },
                                            el(".model-picker-option-icon", Icon("route-line")),
                                            el(".model-picker-option-content",
                                                el("strong", gateway.name || gateway.slug),
                                                el("span", gateway.modelName || gateway.inferenceEndpoint)
                                            ),
                                            el(".model-picker-option-badges",
                                                selectedType.value == "gateway" && selectedGateway.value?.slug == gateway.slug && Icon("check-line")
                                            )
                                        )).nullWhenEmpty() || [el(".model-picker-empty", MutedText("No gateways found."))]
                                    )
                                ))
                            )
                        }
                    ], { type: "fixed" }) :
                    renderModelFinder()
            ),
            el("aside.model-picker-summary", el.state.listening([selectedModel, selectedGateway, selectedType, selectedRoute], () =>
                el(".model-picker-summary-content",
                    selectedType.value == "gateway" && selectedGateway.value ?
                        el.fragment(
                            el(".model-picker-summary-heading",
                                el(".model-picker-summary-icon", Icon("route-line")),
                                el("div",
                                    MutedText("AI Gateway"),
                                    el("h3", selectedGateway.value.name || selectedGateway.value.slug)
                                )
                            ),
                            el("p.model-picker-description", "Routes requests through a configured AI gateway."),
                            el("dl.model-picker-facts",
                                el("div", el("dt", "Slug"), el("dd", selectedGateway.value.slug,
                                    el("button.model-picker-copy[type=button]", { ariaLabel: "Copy gateway slug", title: "Copy gateway slug", onClick: () => copyToClipboard(selectedGateway.value.slug) }, Icon("file-copy-line"))
                                )),
                                selectedGateway.value.modelName && el("div", el("dt", "Model"), el("dd", selectedGateway.value.modelName)),
                                selectedGateway.value.inferenceEndpoint && el("div", el("dt", "Endpoint"), el("dd", selectedGateway.value.inferenceEndpoint))
                            )
                        ) :
                        selectedModel.value ? pipe(selectedModel.value, model => el.fragment(
                            el(".model-picker-summary-heading",
                                el(".model-picker-summary-icon", ModelIcon(model.name)),
                                el("div",
                                    MutedText(model.modelInformation?.provider?.name || model.name.split("/")[0]),
                                    el("h3", model.name.split("/").at(-1))
                                ),
                                el("button.model-picker-copy[type=button]", { ariaLabel: "Copy model name", title: "Copy model name", onClick: () => copyToClipboard(routedName()) }, Icon("file-copy-line"))
                            ),
                            el("p.model-picker-full-name", routedName()),
                            model.description && el("p.model-picker-description", model.description),
                            BadgeList(
                                model.flags?.isFree && Badge("Free", "var(--success)"),
                                (model.flags?.isDiscounted || model.isDiscounted) && Badge(
                                    model.meanDiscount > 0 && model.meanDiscount < 1 ? `${formatNumber(model.meanDiscount * 100)}% off` : "Discounted",
                                    "var(--success)"
                                ),
                                model.flags?.isPreview && Badge("Preview", "var(--fr-300)"),
                                model.flags?.isNew && Badge("New", "var(--primary-color-200)"),
                                model.flags?.isDeprecating && Badge("Deprecating", "var(--warn)")
                            ),
                            model.capabilities?.some(capability => capability != "None") && el(".model-picker-capabilities",
                                el("span.model-picker-capabilities-label", "Capabilities"),
                                el(".model-picker-capability-list", ...model.capabilities.filter(capability => capability != "None").map(ModelCapability))
                            ),
                            el(".model-picker-levels",
                                enumValue(model.intelligenceLevel) && el("div", el("span", "Intelligence"), el("strong", Icon("lightbulb-line"), enumValue(model.intelligenceLevel))),
                                enumValue(model.speedLevel) && el("div", el("span", "Speed"), el("strong", Icon("flashlight-line"), enumValue(model.speedLevel)))
                            ),
                            pipe(routeProviders(model, selectedRoute.value), providers => el.fragment(
                                el(".model-picker-section-title",
                                    el("strong", "Routing"),
                                    MutedText(`${model.providers?.filter(item => !item.isDisabled).length || 0} of ${model.providers?.length || 0} providers online`)
                                ),
                                el(".model-picker-routes", { role: "radiogroup", ariaLabel: "Routing preference" },
                                    ...["", ...RoutingOptions]
                                        .filter(option => option != "Special" || model.providers?.some(provider => enumValue(provider.group) == "Special" && !provider.isDisabled))
                                        .map(option => el("button.model-picker-route[type=button]",
                                            {
                                                role: "radio",
                                                ariaChecked: (selectedRoute.value == option).toString(),
                                                title: option ? `Route each request to the ${option.toLowerCase()} available provider.` : "Use the routing preference of the gateway or workflow.",
                                                onClick: () => selectedRoute.value = option
                                            },
                                            option || "Default"
                                        ))
                                ),
                                Table({ classList: "model-picker-providers" },
                                    TableHeader(TableRow(
                                        TableHeaderCell("Provider"),
                                        TableHeaderCell("In / Out"),
                                        TableHeaderCell("Context"),
                                        TableHeaderCell("Speed"),
                                        TableHeaderCell("Uptime"),
                                        TableHeaderCell()
                                    )),
                                    TableBody(...(model.providers || []).map(provider => pipe(
                                        !provider.isDisabled && enumValue(provider.group) != "Special",
                                        pinnable => TableRow(
                                            {
                                                role: "radio",
                                                tabIndex: pinnable ? 0 : -1,
                                                ariaChecked: (selectedRoute.value == provider.routingTag).toString(),
                                                ariaDisabled: (!pinnable).toString(),
                                                classList: [providers.includes(provider) && "model-picker-provider-eligible"],
                                                title: cond(
                                                    [provider.isDisabled, "This provider is currently unavailable."],
                                                    [!pinnable, "Special providers are reached through the Special route."],
                                                    [true, `Pin requests to ${provider.name} (#${provider.routingTag}).`]
                                                ),
                                                onClick() {
                                                    if (pinnable) {
                                                        selectedRoute.value = selectedRoute.value == provider.routingTag ? "" : provider.routingTag;
                                                    }
                                                },
                                                onKeyDown(event) {
                                                    if (["Enter", " "].includes(event.key)) {
                                                        event.preventDefault();
                                                        this.click();
                                                    }
                                                }
                                            },
                                            TableCell(el(".model-picker-provider-name",
                                                ProviderBadge(provider.name || provider.routingTag),
                                                el("code", `#${provider.routingTag}`)
                                            )),
                                            TableCell(provider.pricing?.[0] ?
                                                `${formatFlexibibleCurrency(provider.pricing[0].inputPerMillionTokens)} / ${formatFlexibibleCurrency(provider.pricing[0].outputPerMillionTokens)}` :
                                                "—"),
                                            TableCell(provider.technicalInfo?.contextWindow != null ? shortSize(provider.technicalInfo.contextWindow) : "—"),
                                            TableCell(
                                                { title: provider.technicalInfo?.latencySeconds != null ? `Median latency: ${formatNumber(provider.technicalInfo.latencySeconds)}s` : "" },
                                                provider.technicalInfo?.throughputTokensPerSecond != null ? `${formatNumber(provider.technicalInfo.throughputTokensPerSecond)} tok/s` : "—"
                                            ),
                                            TableCell(provider.technicalInfo?.uptimePercentage != null ? `${formatNumber(provider.technicalInfo.uptimePercentage)}%` : "—"),
                                            TableCell(
                                                enumValue(provider.technicalInfo?.dataCollection) == "Zero" &&
                                                    TagIconBadge(Icon("shield-check-line"), "ZDR", "var(--success)", { title: "Zero data retention" }),
                                                provider.isDisabled && TagIconBadge(Icon("alert-line"), "Offline", "var(--warn)")
                                            )
                                        )
                                    )))
                                ),
                                providers.length ? el.fragment(
                                    el(".model-picker-section-title",
                                        el("strong", providers.length > 1 ? "Range for this route" : "Details for this route"),
                                        MutedText(`${providers.length} eligible ${providers.length == 1 ? "provider" : "providers"}`)
                                    ),
                                    el("dl.model-picker-facts.model-picker-pricing",
                                        ...[
                                            ["Input", "inputPerMillionTokens"],
                                            ["Cached input", "cachedInputPerMillionTokens"],
                                            ["Output", "outputPerMillionTokens"]
                                        ].map(([label, key]) => pipe(
                                            valueRange(providers.map(provider => provider.pricing?.[0]?.[key]), formatFlexibibleCurrency),
                                            range => range && el("div", el("dt", label), ModelPrice(range))
                                        )),
                                        pipe(valueRange(providers.map(provider => provider.technicalInfo?.contextWindow), shortSize),
                                            range => range && el("div", el("dt", "Context window"), el("dd", `${range} tokens`))),
                                        pipe(valueRange(providers.map(provider => provider.technicalInfo?.maxOutputTokens), shortSize),
                                            range => range && el("div", el("dt", "Max output"), el("dd", `${range} tokens`)))
                                    )
                                ) :
                                    el("p.model-picker-route-warning", Icon("error-warning-line"), "No available provider matches this route right now.")
                            )),
                            (model.modelInformation?.releaseDate || model.modelInformation?.parameterSize) && el.fragment(
                                el(".model-picker-section-title", el("strong", "Model information")),
                                el("dl.model-picker-facts",
                                    model.modelInformation.releaseDate && el("div", el("dt", "Released"), el("dd", model.modelInformation.releaseDate)),
                                    model.modelInformation.parameterSize && el("div", el("dt", "Parameters"), el("dd", model.modelInformation.parameterSize))
                                )
                            )
                        )) :
                            el(".model-picker-summary-empty", Icon("cursor-line"), MutedText("Select a model to view its details."))
                )
            ))
        )
    );
}
